import React, { useState } from 'react';
import {
  Box,
  Typography,
  Grid,
  Modal,
  FormControl,
  TextField,
  Button,
  IconButton,
  Stack
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { FormikProvider, useFormik, Form } from 'formik';
import * as Yup from 'yup';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import CheckCircleOutlineOutlinedIcon from '@mui/icons-material/CheckCircleOutlineOutlined';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';

import { SnackBarAlert } from '../../HelperComponents';
import { ChangePassword } from '../../../network/User';
import { parseValidationErrorMessage } from '../../../utils/Utilities';
import { useAuth } from '../../customContextProviders/AuthContext';
import { passwordRules } from '../../../URLConstants';

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: { xs: '95%', md: '30%' },
  bgcolor: 'background.paper',
  p: 3,
  borderRadius: 1,
  minHeight: '30vh',
  maxHeight: '90vh',
  overflow: 'auto'
};

const changePasswordSchema = Yup.object().shape({
  oldPassword: Yup.string().required('Old password is required'),
  newPassword: Yup.string()
    .required('Password is required')
    .min(8, 'Password must be at least 8 characters')
    .max(20, 'Password must not exceed 20 characters')
    .matches(/^\S*$/, 'Password must not contain spaces')
    .matches(/[!@#$%^&*]/, 'At least 1 special character required')
    .matches(/(?:.*\d){3,}/, 'At least 3 digits required'),

  confirmPassword: Yup.string()
    .oneOf([Yup.ref('newPassword')], 'Passwords must match')
    .required('Please confirm your password')
});

export default function ChangePasswordModal({ openAssignedModal, handleClose }) {
  const { logoutSession } = useAuth();
  const [isSnackBarAlertOpen, setIsSnackBarAlertOpen] = useState(false);
  const [snackBarMetas, setSnackBarMetas] = useState({
    title: '',
    message: '',
    severity: 'info',
    handleClose: null
  });

  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showRules, setShowRules] = useState(false);


  const changePasswordFormik = useFormik({
    initialValues: {
      oldPassword: '',
      newPassword: '',
      confirmPassword: ''
    },
    validationSchema: changePasswordSchema,
    onSubmit: (values) => {
      handleChangePassword(values?.oldPassword, values?.newPassword);
    }
  });

  const { errors, touched, values, handleSubmit } = changePasswordFormik;

  const showErrorState = Boolean(touched.newPassword && errors.newPassword);
  const isPasswordStrong = passwordRules.every((rule) => rule.test(values.newPassword || ''));

  const handleChangePassword = async (oldPassword, newPassword) => {
    try {
      const res = await ChangePassword(oldPassword, newPassword);
      if (res) {
        setSnackBarMetas((prevState) => ({
          ...prevState,
          title: 'Password Changed Successfully !!',
          severity: 'success'
        }));
        setIsSnackBarAlertOpen(true);
        handleModalClose();
        changePasswordFormik.setSubmitting(false);
        setTimeout(() => {
          logoutSession();
        }, 1000);
      }
    } catch (err) {
      setSnackBarMetas((prevState) => ({
        ...prevState,
        title: parseValidationErrorMessage(err),
        severity: 'error'
      }));
      setIsSnackBarAlertOpen(true);
      changePasswordFormik.setSubmitting(false);
    }
  };

  const handleModalClose = () => {
    handleClose();
    changePasswordFormik?.resetForm();
  };

  return (
    <>
      <Modal
        open={openAssignedModal}
        onClose={handleModalClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <FormikProvider value={changePasswordFormik}>
          <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
            <Box sx={style}>
              <Grid container spacing={2}>
                <Grid
                  size={{ xs: 12 }}
                  display="flex"
                  justifyContent="flex-start"
                  direction="column"
                >
                  <Typography sx={{ fontSize: 18, fontWeight: 700, fontFamily: 'Montserrat' }}>
                    Change Password
                  </Typography>
                </Grid>

                {/* OLD PASSWORD */}
                <Grid size={{ xs: 12 }} direction="column" display="flex">
                  <FormControl fullWidth>
                    <TextField
                      autoComplete='new-password'
                      type={showOldPassword ? 'text' : 'password'}
                      label="Old Password"
                      {...changePasswordFormik.getFieldProps('oldPassword')}
                      InputProps={{
                        endAdornment: (
                          <IconButton
                            onClick={() => setShowOldPassword(!showOldPassword)}
                            edge="end"
                          >
                            {showOldPassword ? <Visibility /> : <VisibilityOff />}
                          </IconButton>
                        )
                      }}
                      error={touched.oldPassword && Boolean(errors.oldPassword)}
                      helperText={touched.oldPassword && errors.oldPassword}
                    />
                  </FormControl>
                </Grid>

                {/* NEW PASSWORD */}
                <Grid size={{ xs: 12 }} direction="column" display="flex">
                  <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                    <FormControl fullWidth>
                      <TextField
                        autoComplete='new-password'
                        type={showNewPassword ? 'text' : 'password'}
                        label="New Password"
                        onFocus={() => setShowRules(true)}
                        {...changePasswordFormik.getFieldProps('newPassword')}
                        InputProps={{
                          endAdornment: (
                            <IconButton
                              onClick={() => setShowNewPassword(!showNewPassword)}
                              edge="end"
                            >
                              {showNewPassword ? <Visibility /> : <VisibilityOff />}
                            </IconButton>
                          )
                        }}
                        error={touched.newPassword && Boolean(errors.newPassword)}
                      />
                    </FormControl>
                    {showRules && (
                      <Stack spacing={1} sx={{ mt: 1 }}>
                        {passwordRules.map((rule, index) => {
                          const passed = rule.test(values.newPassword || '');
                          const showError = showErrorState && !passed;

                          let iconColor = 'text.disabled';
                          let textColor = '#9e9e9e';

                          if (passed) {
                            iconColor = 'success.main';
                            textColor = '#54D62C';
                          } else if (showError) {
                            iconColor = '#FF0000';
                            textColor = '#FF0000';
                          }

                          return (
                            <Stack key={index} direction="row" spacing={1} alignItems="center">
                              {passed ? (
                                <CheckCircleOutlineOutlinedIcon sx={{ color: iconColor, fontSize: 18 }} />
                              ) : (
                                <CancelOutlinedIcon sx={{ color: iconColor, fontSize: 18 }} />
                              )}

                              <span style={{ color: textColor, fontSize: '14px' }}>
                                {rule.label}
                              </span>
                            </Stack>
                          );
                        })}
                      </Stack>
                    )}
                  </Box>

                </Grid>

                <Grid size={{ xs: 12 }} direction="column" display="flex">
                  <FormControl fullWidth>
                    <TextField
                      autoComplete='new-password'
                      type={showNewPassword ? 'text' : 'password'}
                      label="Confirm Password"
                      {...changePasswordFormik.getFieldProps('confirmPassword')}
                      InputProps={{
                        endAdornment: (
                          <IconButton
                            onClick={() => setShowNewPassword(!showNewPassword)}
                            edge="end"
                          >
                            {showNewPassword ? <Visibility /> : <VisibilityOff />}
                          </IconButton>
                        )
                      }}
                      error={touched.confirmPassword && Boolean(errors.confirmPassword)}
                      helperText={touched.confirmPassword && errors.confirmPassword}

                    />
                  </FormControl>
                </Grid>

                <Grid size={{ xs: 12 }} sx={{ mt: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Button
                      sx={{ mt: 2, height: '40px', borderRadius: 2, mr: 2 }}
                      variant="outlined"
                      size="large"
                      onClick={handleModalClose}
                    >
                      Cancel
                    </Button>

                    <LoadingButton
                      loading={changePasswordFormik?.isSubmitting}
                      sx={{ mt: 2, height: '40px', borderRadius: 2 }}
                      variant="contained"
                      type="submit"
                      disabled={!isPasswordStrong}
                      size="large"
                    >
                      Change
                    </LoadingButton>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </Form>
        </FormikProvider>
      </Modal>

      <SnackBarAlert
        open={isSnackBarAlertOpen}
        title={snackBarMetas?.title}
        message={snackBarMetas?.message}
        severity={snackBarMetas?.severity}
        handleClose={() => setIsSnackBarAlertOpen(false)}
      />
    </>
  );
}